import { getCroppedImg } from "../../utils/profileImage";
import React, { useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Cropper from "react-easy-crop";
import {
  Plus,
  Sparkles,
  ArrowLeft,
  Loader2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "./UserDetailsStepTwoPage.css";
import nextIcon from "../../assets/nextButtonIcon.png";
import nextIcon2 from "../../assets/nextButtonIcon2.png";
import apiClient from "../../api/apiClient";
import { ENDPOINTS } from "../../api/endPoints";
import { useGlobalAlert } from "../../context/AlertContext";
import MobileTopBar from "../../components/MobileTopBar";
import MobileBottomBar from "../../components/MobileBottomBar";
import DesktopTopBar from "../../components/DesktopTopBar";
import DesktopBottomBar from "../../components/DesktopBottomBar";



const UserDetailsStepTwoPage = ({ formData, setFormData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { triggerAlert } = useGlobalAlert();

  const [loadingBio, setLoadingBio] = useState(false);
  const [mobileStep, setMobileStep] = useState(location.state?.step || 1);

  // Refs
  const mainImageRef = useRef(null);
  const subImageRefs = useRef([]);

  // Local image previews
  const [images, setImages] = useState(() => {
    if (formData.photos) {
      const previews = {};
      Object.entries(formData.photos).forEach(([key, file]) => {
        if (file instanceof File) {
          previews[key] = URL.createObjectURL(file);
        } else if (typeof file === "string") {
          previews[key] = file;
        }
      });
      return { main: null, sub1: null, sub2: null, sub3: null, ...previews };
    }
    return { main: null, sub1: null, sub2: null, sub3: null };
  });

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropKey, setCropKey] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const handleWriteBio = async () => {
    setLoadingBio(true);
    try {
      const payload = {
        name: formData.name || "User",
        what_brings_you_here: formData.selectedBringsYouHere || "",
      };

      const response = await apiClient.post(
        ENDPOINTS.GENERATE_AI_BIO_TEAMSOCIAL,
        payload,
      );

      if (response.data.success && response.data.bio) {
        setFormData((prev) => ({
          ...prev,
          userBio: response.data.bio,
        }));
      }
    } catch (error) {
      console.error("AI Bio Error:", error.response?.data || error.message);
      triggerAlert("Could not generate bio at this time.", "error");
    } finally {
      setLoadingBio(false);
    }
  };

  // Handle Image Selection and open cropper
  const handleImageSelect = (event, key) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setCropImageSrc(reader.result);
        setCropKey(key);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCropModalOpen(true);
      };
      event.target.value = "";
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPx) => {
    setCroppedAreaPixels(croppedAreaPx);
  }, []);

  const handleSaveCrop = async () => {
    try {
      const croppedFile = await getCroppedImg(
        cropImageSrc,
        croppedAreaPixels,
        cropKey,
      );
      if (croppedFile) {
        const imageUrl = URL.createObjectURL(croppedFile);

        setImages((prev) => ({ ...prev, [cropKey]: imageUrl }));

        setFormData((prev) => ({
          ...prev,
          photos: { ...prev.photos, [cropKey]: croppedFile },
        }));
      }
    } catch (e) {
      console.error("Error generating crop", e);
    } finally {
      setCropModalOpen(false);
      setCropImageSrc(null);
    }
  };

  const handleCancelCrop = () => {
    setCropModalOpen(false);
    setCropImageSrc(null);
  };

  // Handle Bio Input
  const handleBioChange = (e) => {
    setFormData((prev) => ({ ...prev, userBio: e.target.value }));
  };

  // Validation
  const hasAtLeastOneImage = () => {
    if (!formData.photos) return false;
    return ["main", "sub1", "sub2", "sub3"].some((key) => formData.photos[key]);
  };

  const isBioValid = () => {
    return formData.userBio && formData.userBio.trim().length > 0;
  };

  const isNextDisabled = !hasAtLeastOneImage() || !isBioValid();

  const handleNext = () => {
    if (window.innerWidth >= 1024) {
      if (isNextDisabled) return;
      console.log("Form Data Final:", formData);
      navigate("/onboarding-final");
    } else {
      if (!isBioValid()) return;
      console.log("Form Data Final:", formData);
      navigate("/onboarding-final");
    }
  };

  const handleSkip = () => {
    navigate("/onboarding-final");
    console.log("Form Data Final:", formData);
  };

  return (
    <div
      className={`uds2-container ${mobileStep === 1 ? "uds2-mobile-step-1" : "uds2-mobile-step-2"}`}
    >
      {/* LEFT PANEL (Green/Photos) - Top on Mobile / Step 1 */}
      <div
        className={`uds2-left-panel ${mobileStep === 1 ? "uds2-mobile-active" : "uds2-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => navigate("/user-details-step-one")}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
          isProtectedRoute={false}
        />

        {/* Desktop Back Button */}
        {/* <button
          className="login-back-btn uds2-desktop-only"
          onClick={() => navigate("/user-details-step-one")}
        >
          <ArrowLeft size={24} color="#000" strokeWidth={3} />
          <span style={{ color: "#000" }}>Back</span>
        </button> */}
        <DesktopTopBar
          showBack={true}
          onBack={() => navigate("/user-details-step-one")}
          backColor="#000"
          showProgress={false}
        />

        <div className="uds2-content-wrapper centered-text">
          <div className="uds2-header-group">
            <h1 className="uds2-title text-black">YOUR PICS</h1>
            <p className="uds2-subtitle text-black">
              A happy headshot, a family pic with {formData.dogName || "Dimsum"}
              , and doing what you do in the world, are all great choices!
            </p>
          </div>

          {/* PHOTO GRID */}
          <div className="uds2-photo-grid">
            {/* Main Large Image */}
            <div
              className="uds2-photo-box uds2-main-photo"
              onClick={() => mainImageRef.current.click()}
            >
              <input
                type="file"
                hidden
                ref={mainImageRef}
                accept="image/*"
                onChange={(e) => handleImageSelect(e, "main")}
              />
              {images.main ? (
                <img
                  src={images.main}
                  alt="Main Upload"
                  className="uds2-uploaded-img"
                />
              ) : (
                <Plus size={40} color="#000" />
              )}
            </div>

            {/* Sub Images Row */}
            <div className="uds2-sub-photos-row">
              {["sub1", "sub2", "sub3"].map((key, index) => (
                <div
                  key={key}
                  className="uds2-photo-box uds2-sub-photo"
                  onClick={() => subImageRefs.current[index].click()}
                >
                  <input
                    type="file"
                    hidden
                    ref={(el) => (subImageRefs.current[index] = el)}
                    accept="image/*"
                    onChange={(e) => handleImageSelect(e, key)}
                  />
                  {images[key] ? (
                    <img
                      src={images[key]}
                      alt="Sub Upload"
                      className="uds2-uploaded-img"
                    />
                  ) : (
                    <Plus size={32} color="#000" />
                  )}
                </div>
              ))}
            </div>
          </div>
          <MobileBottomBar
            onNext={() => hasAtLeastOneImage() && setMobileStep(2)}
            isNextEnabled={hasAtLeastOneImage()}
            nextIconSrc={nextIcon2}
            variant="blue"
          />
        </div>
      </div>

      {/* RIGHT PANEL (Blue/Bio) - Bottom on Mobile / Step 2 */}
      <div
        className={`uds2-right-panel ${mobileStep === 2 ? "uds2-mobile-active" : "uds2-mobile-hidden"}`}
      >
        <MobileTopBar
          onBack={() => setMobileStep(1)}
          backColor="#fff"
          logoColor="#fff"
          isProtectedRoute={false}
        />

        <div className="uds2-content-wrapper">
          <h1 className="uds2-title text-white">IN YOUR OWN WORDS</h1>

          <p className="uds2-subtitle text-light-blue">
            Share more about what brings you to Social Paws,
            {formData.dogName ? ` ${formData.dogName}'s` : " [Dog Name]'s"}{" "}
            greatest traits, and whatever else your heart desires!
          </p>

          <div className="uds2-bio-wrapper">
            <textarea
              className="uds2-bio-textarea"
              placeholder="Write here..."
              value={formData.userBio || ""}
              onChange={handleBioChange}
            />

            <div className="uds2-action-group">
              <button
                type="button"
                className="uds2-write-btn"
                onClick={handleWriteBio}
                disabled={loadingBio}
              >
                {loadingBio ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Sparkles size={20} strokeWidth={2} />
                )}
                {loadingBio ? "GENERATING..." : "WRITE MY BIO"}
              </button>
              <p className="uds2-ai-help-text">
                Want a helping paw?
                <br />
                Let AI write the bio!
                <br />
                you can edit before saving
              </p>
            </div>
          </div>

          {/* Footer Navigation */}
          {/* <div className="uds2-footer desktop-only">
            <button onClick={handleSkip} className="uds2-skip-btn">
              SKIP
            </button>

            <button
              onClick={
                window.innerWidth >= 1024
                  ? !isNextDisabled
                    ? handleNext
                    : undefined
                  : isBioValid()
                    ? handleNext
                    : undefined
              }
              className={`uds2-next-btn-group ${
                window.innerWidth >= 1024
                  ? isNextDisabled
                    ? "disabled"
                    : ""
                  : !isBioValid()
                    ? "disabled"
                    : ""
              }`}
            >
              <span
                className="uds2-next-text"
                style={{ color: "rgba(182, 252, 238, 1)" }}
              >
                NEXT
              </span>
              <div
                className="uds2-next-circle"
                style={{ backgroundColor: "rgba(255, 255, 255, 0.15)" }}
              >
                <img
                  src={nextIcon}
                  alt="Next Icon"
                  className="uds2-next-icon"
                />
              </div>
            </button>
          </div> */}
          <DesktopBottomBar
            onNext={handleNext}
            isNextEnabled={!isNextDisabled}
            nextIconSrc={nextIcon}
            showSkip={false}
            // onSkip={handleSkip}
            variant="green"
          />

          <MobileBottomBar
            onNext={handleNext}
            isNextEnabled={isBioValid()}
            nextIconSrc={nextIcon}
            showSkip={false}
            // onSkip={handleSkip}
            variant="green"
          />
        </div>
      </div>

      {/* Custom Cropper Modal */}
      {cropModalOpen && cropImageSrc && (
        <div className="crop-modal-overlay">
          <div className="crop-modal-box">
            <button className="crop-close-btn" onClick={handleCancelCrop}>
              <X size={20} color="rgba(166, 28, 31, 1)" strokeWidth={3} />
            </button>

            <h2 className="crop-modal-title">Upload Picture</h2>

            <div className="crop-container">
              <Cropper
                image={cropImageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                classes={{
                  containerClassName: "custom-cropper-container",
                }}
                cropAreaStyle={{
                  border: "2px dashed white",
                }}
              />
            </div>

            <div className="crop-slider-wrapper">
              <ZoomOut size={24} color="#000" />
              <input
                type="range"
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                aria-labelledby="Zoom"
                onChange={(e) => setZoom(e.target.value)}
                className="crop-slider"
              />
              <ZoomIn size={24} color="#000" />
            </div>

            <div className="crop-action-row">
              <button
                className="crop-btn-change"
                onClick={() => {
                  if (cropKey === "main") {
                    mainImageRef.current.click();
                  } else {
                    const index = parseInt(cropKey.replace("sub", "")) - 1;
                    subImageRefs.current[index].click();
                  }
                }}
              >
                CHANGE IMAGE
              </button>
              <button className="crop-btn-save" onClick={handleSaveCrop}>
                CROP & SAVE
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetailsStepTwoPage;
